import { Injector, inject } from '@angular/core';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { Observable, noop, of } from 'rxjs';
import { ConfigurationService } from '@sc/data-access-configuration';
import { DialogService } from '@sc/feature-dialog';
import { Configuration } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';

export function getInitConfigFactory(
  afterConfigInit?: (injector: Injector) => Observable<any>
) {
  return () => {
    const configService = inject(ConfigurationService);
    const translatorService = inject(TranslatorService);
    const dialog = inject(DialogService);
    const injector = inject(Injector);

    return () => {
      return configService.init$().pipe(
        take(1),
        tap(noop, (err) => {
          console.error('!!! Config Load Error :', err);
          dialog.showError(
            'Error',
            'The application is currently unavailable. We apologize for the inconvenience. Please try later.',
            [{ buttonChoice: 'CLOSE', buttonText: 'Close' }]
          );
        }),
        catchError(() => of<Configuration>(null)),
        switchMap(() => translatorService.init$().pipe(take(1))),
        switchMap(() => afterConfigInit?.(injector).pipe(take(1)) || of(null))
      );
    };
  };
}

export function initAccessibilityObserversFactory() {
  return () => {
    const MutationObeserver = window.MutationObserver;
    const observer = new MutationObeserver((mutations, obs) => {
      mutations
        .filter((mutation) => mutation.target.nodeType === 1)
        .forEach((mutation) => {
          const targetMut = mutation.target as Element;
          switch (targetMut.nodeName.toLowerCase()) {
            case 'form':
              if (targetMut.hasAttribute('aria-description')) {
                targetMut.removeAttribute('aria-description');
              }
              break;
            case 'label':
              if (targetMut.hasAttribute('aria-owns')) {
                targetMut.removeAttribute('aria-owns');
              }
              break;
            case 'svg':
              if (targetMut.getAttribute('aria-hidden') !== 'true') {
                targetMut.setAttribute('aria-hidden', 'true');
              }
              break;
          }
        });
    });
    observer.observe(document, {
      subtree: true,
      childList: true,
      attributes: true,
    });
  };
}
